import { useTranslate } from '@refinedev/core';
import { Avatar, Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { UserOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { UserRoleTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';
import { USER_SHOW_TABS, getUserDisplayName } from '../constants';
import type { UserShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<UserShowTab, string> = {
  general: 'iam.users.tabs.general',
  consents: 'iam.users.tabs.consents',
  oauth_accounts: 'iam.users.tabs.oauth_accounts',
  oauth_tokens: 'iam.users.tabs.oauth_tokens',
  fcm_tokens: 'iam.users.tabs.fcm_tokens',
  licences: 'iam.users.tabs.licences',
  entitlements: 'iam.users.tabs.entitlements',
};

const getInitials = (record: UserOut) =>
  record.first_name || record.last_name
    ? `${record.first_name?.[0] ?? ''}${record.last_name?.[0] ?? ''}`.toUpperCase()
    : (record.username[0] ?? '?').toUpperCase();

interface UserShowHeaderProps {
  record: UserOut;
  activeTab: UserShowTab;
  onTabChange: (key: string) => void;
}

export const UserShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: UserShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <Avatar
          size={64}
          style={{
            backgroundColor: token.colorPrimary,
            fontSize: token.fontSizeHeading4,
          }}
        >
          {getInitials(record)}
        </Avatar>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {getUserDisplayName(record)}
            </Title>
            <UserRoleTag role={record.role} />
            <Tag
              icon={
                record.is_active ? <CheckCircleOutlined /> : <StopOutlined />
              }
              color={record.is_active ? 'success' : 'error'}
            >
              {t(`iam.users.status.${record.is_active ? 'active' : 'blocked'}`)}
            </Tag>
            <Tag
              icon={
                record.is_verified ? (
                  <CheckCircleOutlined />
                ) : (
                  <CloseCircleOutlined />
                )
              }
              color={record.is_verified ? 'processing' : 'warning'}
            >
              {t(
                `iam.users.status.${record.is_verified ? 'verified' : 'unverified'}`
              )}
            </Tag>
            <Tag color={record.is_anonymous ? 'default' : 'cyan'}>
              {t(
                `iam.users.status.${record.is_anonymous ? 'anonymous' : 'registered'}`
              )}
            </Tag>
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id} · @${record.username}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('iam.users.fields.email')}>
              {orDash(
                record.email ? <Text copyable>{record.email}</Text> : null
              )}
            </StatCell>
            <StatCell label={t('iam.users.tabs.profiles')}>
              {record.profiles?.length ?? 0}
            </StatCell>
            <StatCell label={t('base.fields.created_at')}>
              {orDash(formatDateTime(record.created_at))}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={USER_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
